import "./white-voice-chart.css";
import {
  plainTextFromClinicalContent,
  type ClinicalDocumentContent,
  type ClinicalParagraph,
  type ClinicalTextRun,
} from "../shared/clinical";
import type {
  ClinicalFactKey,
  ClinicalFactSuggestion,
} from "../shared/voice-charting";

export const clinicalNoteSections = Object.freeze([
  {
    id: "chief_complaint",
    label: "Chief Complaint",
    description: "주호소",
  },
  {
    id: "onset",
    label: "Onset",
    description: "발병 시점",
  },
  {
    id: "vector_history",
    label: "Vector History",
    description: "외상·손상기전",
  },
  {
    id: "history",
    label: "History",
    description: "현병력·과거력",
  },
  {
    id: "physical_examination",
    label: "Physical Examination",
    description: "진찰 소견",
  },
  {
    id: "order",
    label: "Order",
    description: "검사·치료 오더",
  },
  {
    id: "procedure",
    label: "Procedure",
    description: "처치·시술",
  },
] as const);

export type ClinicalNoteSectionId = (typeof clinicalNoteSections)[number]["id"];

const sectionHeadingTexts = new Set(
  clinicalNoteSections.map((section) => sectionHeadingText(section.id)),
);

function cloneRun(run: ClinicalTextRun): ClinicalTextRun {
  return { ...run };
}

function cloneBlock(block: ClinicalParagraph): ClinicalParagraph {
  return { type: "paragraph", runs: block.runs.map(cloneRun) };
}

export function sectionHeadingText(sectionId: ClinicalNoteSectionId): string {
  const section = clinicalNoteSections.find((candidate) => candidate.id === sectionId);
  if (!section) throw new Error(`Unknown clinical note section: ${sectionId}`);
  return `${section.label} · ${section.description}`;
}

function headingBlock(sectionId: ClinicalNoteSectionId): ClinicalParagraph {
  return {
    type: "paragraph",
    runs: [
      {
        text: sectionHeadingText(sectionId),
        bold: true,
        color: "blue",
        size: "large",
      },
    ],
  };
}

function blankBodyBlock(): ClinicalParagraph {
  return { type: "paragraph", runs: [{ text: "" }] };
}

export function createStructuredClinicalDraft(): ClinicalDocumentContent {
  return {
    schemaVersion: 1,
    blocks: clinicalNoteSections.flatMap((section) => [
      headingBlock(section.id),
      blankBodyBlock(),
    ]),
  };
}

function blockText(block: ClinicalParagraph): string {
  return block.runs.map((run) => run.text).join("");
}

function isHeading(block: ClinicalParagraph): boolean {
  return sectionHeadingTexts.has(blockText(block));
}

function headingIndex(
  content: ClinicalDocumentContent,
  sectionId: ClinicalNoteSectionId,
): number {
  const expected = sectionHeadingText(sectionId);
  return content.blocks.findIndex((block) => blockText(block) === expected);
}

function sectionEndIndex(
  content: ClinicalDocumentContent,
  sectionId: ClinicalNoteSectionId,
): number {
  const start = headingIndex(content, sectionId);
  if (start < 0) return -1;
  const next = content.blocks.findIndex(
    (block, index) => index > start && isHeading(block),
  );
  return next < 0 ? content.blocks.length : next;
}

function hasCompleteTemplate(content: ClinicalDocumentContent): boolean {
  return clinicalNoteSections.every((section) => headingIndex(content, section.id) >= 0);
}

function containsMeaningfulText(content: ClinicalDocumentContent): boolean {
  return plainTextFromClinicalContent(content).trim().length > 0;
}

export function ensureStructuredClinicalDraft(
  content: ClinicalDocumentContent,
): ClinicalDocumentContent {
  if (hasCompleteTemplate(content)) {
    return {
      schemaVersion: 1,
      blocks: content.blocks.map(cloneBlock),
    };
  }

  const template = createStructuredClinicalDraft();
  if (!containsMeaningfulText(content)) return template;

  const historyHeading = headingIndex(template, "history");
  template.blocks.splice(
    historyHeading + 1,
    1,
    ...content.blocks.map(cloneBlock),
  );
  return template;
}

export function clinicalFactSection(
  key: ClinicalFactKey,
): ClinicalNoteSectionId {
  switch (key) {
    case "chief_complaint":
      return "chief_complaint";
    case "onset":
      return "onset";
    case "laterality":
    case "anatomical_site":
    case "trauma":
    case "aggravating_factor":
    case "vector_history":
      return "vector_history";
    case "physical_examination":
      return "physical_examination";
    case "order":
      return "order";
    case "procedure":
      return "procedure";
    case "associated_symptom":
    case "history":
    default:
      return "history";
  }
}

function appendFactRuns(
  body: ClinicalParagraph,
  facts: readonly ClinicalFactSuggestion[],
): void {
  const hasExistingText = body.runs.some((run) => run.text.trim().length > 0);
  facts.forEach((fact, index) => {
    if (hasExistingText || index > 0) body.runs.push({ text: "\n" });
    body.runs.push(
      { text: `${fact.label}: `, bold: true, color: "blue" },
      { text: fact.value, keyword: true },
    );
  });
}

export function appendClinicalFactsToStructuredDraft(
  content: ClinicalDocumentContent,
  facts: readonly ClinicalFactSuggestion[],
): ClinicalDocumentContent {
  const next = ensureStructuredClinicalDraft(content);
  if (facts.length === 0) return next;

  const grouped = new Map<ClinicalNoteSectionId, ClinicalFactSuggestion[]>();
  for (const fact of facts) {
    const sectionId = clinicalFactSection(fact.key);
    const list = grouped.get(sectionId) ?? [];
    list.push(fact);
    grouped.set(sectionId, list);
  }

  for (const section of clinicalNoteSections) {
    const sectionFacts = grouped.get(section.id);
    if (!sectionFacts?.length) continue;
    const start = headingIndex(next, section.id);
    const end = sectionEndIndex(next, section.id);
    if (start < 0 || end < 0) continue;

    let bodyIndex = end - 1;
    if (bodyIndex <= start || isHeading(next.blocks[bodyIndex]!)) {
      next.blocks.splice(end, 0, blankBodyBlock());
      bodyIndex = end;
    }
    appendFactRuns(next.blocks[bodyIndex]!, sectionFacts);
  }

  return next;
}

export function countStructuredVoiceEntries(
  content: ClinicalDocumentContent,
): number {
  let count = 0;
  for (const block of content.blocks) {
    if (isHeading(block)) continue;
    for (const run of block.runs) {
      if (run.keyword === true && run.text.trim().length > 0) count += 1;
    }
  }
  return count;
}
